/* Client-side mirror of the server-authoritative profile. The server is the
 * source of truth: it sends `profile` messages, we cache the latest snapshot and
 * the account token (persisted in localStorage only to re-identify on reconnect).
 * All mutations are requests to the server via net; nothing here changes state
 * except applying what the server sends back. */
import {
  levelOf as engineLevelOf, ownedHeroIdsOf, passTierOf, type Notice, type ProfileData,
} from '@aleaces/engine';
import { toast } from './toast';

const TOKEN_KEY = 'aleaces_token';

let profile: ProfileData | null = null;
let questResets: QuestResets | null = null;
const listeners = new Set<() => void>();

/** Server-authoritative quest reset times (epoch ms) from the profile message. */
export interface QuestResets {
  daily: number;
  weekly: number;
}

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch { /* storage blocked */ }
}

export function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch { /* ignore */ }
}

export function getProfile(): ProfileData | null {
  return profile;
}

export function subscribeProfile(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Applies a `profile` message from the server and surfaces its notices. */
export function applyServerProfile(next: ProfileData, notices: Notice[], resets?: QuestResets): void {
  profile = next;
  if (resets) questResets = resets;
  for (const n of notices) toast(n.title, n.sub);
  for (const fn of listeners) fn();
}

export function getQuestResets(): QuestResets | null {
  return questResets;
}

export function isReady(): boolean {
  return profile !== null;
}

// ---------- derived helpers ----------
export const levelOf = engineLevelOf;
export const passTier = (p: ProfileData): number => passTierOf(p.passXP);

export function owns(type: keyof ProfileData['owned'], id: string, p: ProfileData | null = profile): boolean {
  return !!p && (p.owned[type] ?? []).includes(id);
}

export function ownedHeroIds(p: ProfileData | null = profile): string[] {
  return p ? ownedHeroIdsOf(p) : [];
}
